type LogLevel = 'debug' | 'info' | 'warn' | 'error';
interface LoggerConfig {
  enabled: boolean;
  minLevel: LogLevel;
  prefix?: string;
}
const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};
class Logger {
  private config: LoggerConfig;
  constructor(config?: Partial<LoggerConfig>) {
    const env = import.meta.env.VITE_ENVIRONMENT || 'local';
    const nodeEnv = import.meta.env.NODE_ENV;

    // Default: enable logging in local/dev/ci, disable in staging/prod
    const defaultEnabled =
      ['local', 'dev', 'ci', 'test'].includes(env) || nodeEnv === 'development';

    // Default: show all logs in dev, only warn+ in production
    const defaultMinLevel: LogLevel = env === 'prod' || nodeEnv === 'production' ? 'warn' : 'debug';
    this.config = {
      enabled: defaultEnabled,
      minLevel: defaultMinLevel,
      ...config,
    };
  }
  private shouldLog(level: LogLevel): boolean {
    if (!this.config.enabled) return false;
    return LOG_LEVELS[level] >= LOG_LEVELS[this.config.minLevel];
  }
  private formatMessage(level: LogLevel, message: string, context?: string): string {
    const prefix = this.config.prefix || context || '';
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0]; // HH:MM:SS
    return prefix ? `[${timestamp}] [${prefix}] ${message}` : `[${timestamp}] ${message}`;
  }
  debug(message: string, ...args: unknown[]): void {
    if (this.shouldLog('debug')) {
      console.debug(this.formatMessage('debug', message), ...args);
    }
  }
  info(message: string, ...args: unknown[]): void {
    if (this.shouldLog('info')) {
      console.info(this.formatMessage('info', message), ...args);
    }
  }
  warn(message: string, ...args: unknown[]): void {
    if (this.shouldLog('warn')) {
      console.warn(this.formatMessage('warn', message), ...args);
    }
  }
  error(message: string, error?: unknown, ...args: unknown[]): void {
    if (this.shouldLog('error')) {
      console.error(this.formatMessage('error', message), error, ...args);
    }
  }
  child(prefix: string): Logger {
    const _prefix = this.config.prefix ? this.config.prefix + '.' + prefix : prefix;
    return new Logger({ ...this.config, prefix: _prefix });
  }
}
export const logger = new Logger();
export { Logger };
